import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatEUR, formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export const REQ_STATUS = {
  draft: ["Entwurf", "bg-slate-100 text-slate-700 border-slate-200"],
  pending_approval: ["Zur Freigabe", "bg-amber-50 text-amber-800 border-amber-200"],
  approved: ["Freigegeben", "bg-sky-50 text-sky-800 border-sky-200"],
  sent: ["Versendet – Zustimmung ausstehend", "bg-indigo-50 text-indigo-800 border-indigo-200"],
  consent_received: ["Zustimmung eingegangen (prüfen)", "bg-emerald-50 text-emerald-800 border-emerald-300"],
  agreed: ["Zugestimmt", "bg-emerald-50 text-emerald-800 border-emerald-200"],
  partially_agreed: ["Teilweise zugestimmt", "bg-teal-50 text-teal-800 border-teal-200"],
  rejected: ["Abgelehnt", "bg-rose-50 text-rose-800 border-rose-200"],
  applied: ["Wirksam / abgeschlossen", "bg-emerald-600 text-white border-emerald-600"],
  cancelled: ["Storniert", "bg-slate-50 text-slate-400 border-slate-200 line-through"],
};
const LEGACY = { prepared: "draft", prepared_editor: "draft", sent_print: "draft" };
export const normStatus = (s) => LEGACY[s] || s || "draft";
export const METHODS = [["post", "Post"], ["einschreiben_einwurf", "Einschreiben Einwurf"], ["einschreiben_uebergabe", "Einschreiben (Übergabe)"],
  ["einschreiben_rueckschein", "Einschreiben Rückschein"], ["einschreiben_eigenhaendig", "Einschreiben Eigenhändig"], ["email", "E-Mail"],
  ["bote", "Bote"], ["persoenlich", "Persönliche Übergabe"], ["portal", "Mieterportal"]];
const isRegistered = (m) => (m || "").startsWith("einschreiben");
const today = () => new Date().toISOString().slice(0, 10);
const err = (e) => toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen");

export function ReqStatus({ status, testId }) {
  const [l, c] = REQ_STATUS[normStatus(status)] || [status, ""];
  return <span className={`rounded-full px-2 py-0.5 border text-[11px] font-medium ${c}`} data-testid={testId}>{l}</span>;
}

export function DispatchDialog({ req, open, onClose, onDone }) {
  const [f, setF] = useState({ method: "post", sent_date: today(), delivered_date: "", note: "", tracking_number: "" });
  const [proof, setProof] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rent-increase/requests/${req.id}/dispatch`, { ...f, delivered_date: f.delivered_date || null, tracking_number: isRegistered(f.method) && f.tracking_number.trim() ? f.tracking_number : null });
      if (proof) {
        const fd = new FormData(); fd.append("file", proof);
        await api.post(`/rent-increase/requests/${req.id}/proof`, fd);
      }
      toast.success(`Versand dokumentiert · Zustimmungsfrist ${formatDate(data.consent_deadline)} · wirksam ab ${formatDate(data.effective_date)}`);
      onDone(); onClose();
    } catch (e) { err(e); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ri-dispatch-dialog">
        <DialogHeader>
          <DialogTitle>Versand dokumentieren</DialogTitle>
          <DialogDescription>Aus dem Zugangsdatum berechnet Domivus Zustimmungsfrist (Ende 2. Monat nach Zugang), Wirksamkeit (Beginn 3. Monat) und Klagefrist (§ 558b BGB). Ohne Zugangsdatum gilt das Versanddatum.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="col-span-2"><Label className="text-xs">Versandart</Label>
            <select className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm" value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} data-testid="ri-dispatch-method">
              {METHODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></div>
          {isRegistered(f.method) && <div className="col-span-2"><Label className="text-xs">Sendungsnummer (wird über die Deutsche Post verfolgt – Zustellung setzt das Zugangsdatum automatisch)</Label>
            <Input value={f.tracking_number} onChange={(e) => setF({ ...f, tracking_number: e.target.value })} placeholder="z. B. RR 1234 5678 9DE" data-testid="ri-dispatch-tracking" /></div>}
          <div><Label className="text-xs">Versanddatum</Label><Input type="date" value={f.sent_date} onChange={(e) => setF({ ...f, sent_date: e.target.value })} data-testid="ri-dispatch-sent" /></div>
          <div><Label className="text-xs">Zugangsdatum (falls bekannt)</Label><Input type="date" value={f.delivered_date} onChange={(e) => setF({ ...f, delivered_date: e.target.value })} data-testid="ri-dispatch-delivered" /></div>
          <div className="col-span-2"><Label className="text-xs">Zugangsnachweis (optional – Einlieferungsbeleg, Botenprotokoll …)</Label>
            <Input type="file" accept="application/pdf,image/*" onChange={(e) => setProof(e.target.files?.[0] || null)} data-testid="ri-dispatch-proof" /></div>
          <div className="col-span-2"><Label className="text-xs">Notiz</Label><Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="ri-dispatch-note" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Abbrechen</Button><Button onClick={save} disabled={busy || !f.sent_date} data-testid="ri-dispatch-save">Versand speichern</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ResponseDialog({ req, open, onClose, onDone }) {
  const [f, setF] = useState({ result: req?.status === "consent_received" ? "agreed" : "agreed", response_date: today(), agreed_rent: "", note: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rent-increase/requests/${req.id}/response`, { ...f, agreed_rent: f.agreed_rent ? Number(String(f.agreed_rent).replace(",", ".")) : null });
      toast.success(data.applied ? "Antwort dokumentiert – neue Miete übernommen" : "Antwort dokumentiert");
      onDone(); onClose();
    } catch (e) { err(e); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ri-response-dialog">
        <DialogHeader>
          <DialogTitle>Antwort des Mieters erfassen</DialogTitle>
          <DialogDescription>Bei (Teil-)Zustimmung übernimmt Domivus die neue Nettokaltmiete automatisch zum Wirksamkeitsdatum {formatDate(req?.effective_date)} und aktualisiert die Sollstellungen.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex flex-wrap gap-2">
            {[["agreed", "Zugestimmt"], ["partially_agreed", "Teilweise zugestimmt"], ["rejected", "Abgelehnt"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setF({ ...f, result: k })} data-testid={`ri-response-${k}`}
                className={`px-3 py-1.5 rounded-md border text-xs ${f.result === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-300"}`}>{l}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Datum der Antwort</Label><Input type="date" value={f.response_date} onChange={(e) => setF({ ...f, response_date: e.target.value })} data-testid="ri-response-date" /></div>
            {f.result === "partially_agreed" && <div><Label className="text-xs">Zugestimmte Miete (€)</Label><Input value={f.agreed_rent} onChange={(e) => setF({ ...f, agreed_rent: e.target.value })} placeholder={`zwischen ${req?.current_rent} und ${req?.new_rent}`} data-testid="ri-response-agreed-rent" /></div>}
          </div>
          {f.result === "rejected" && <div className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded px-2 py-1.5">Nach § 558b Abs. 2 BGB kann bis {formatDate(req?.klage_deadline)} auf Zustimmung geklagt werden. Domivus erinnert rechtzeitig vor Ablauf.</div>}
          <div><Label className="text-xs">Notiz</Label><Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="ri-response-note" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Abbrechen</Button><Button onClick={save} disabled={busy} data-testid="ri-response-save">Speichern</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RequestHistory({ history }) {
  if (!history?.length) return null;
  return (
    <ol className="mt-1.5 space-y-0.5 border-l border-slate-200 pl-3" data-testid="ri-request-history">
      {[...history].reverse().map((h, i) => (
        <li key={i} className="text-[11px] text-slate-600"><span className="text-slate-400">{formatDate(h.at)} {String(h.at).slice(11, 16)} · {h.by_name}</span> — {h.text}</li>
      ))}
    </ol>
  );
}

export function Fristen({ r }) {
  return (
    <span className="text-[11px] text-slate-500">
      {r.dispatch && <>Versand {formatDate(r.dispatch.sent_date)}{!r.dispatch.delivered_assumed && r.dispatch.delivered_date ? ` · Zugang ${formatDate(r.dispatch.delivered_date)}` : ""}{r.shipment?.tracking_number ? ` · Sendungsnr. ${r.shipment.tracking_number}` : ""} · </>}
      Zustimmung bis {formatDate(r.consent_deadline)} · wirksam ab {formatDate(r.effective_date)}{r.klage_deadline ? ` · Klagefrist ${formatDate(r.klage_deadline)}` : ""}
    </span>
  );
}

export const rentLine = (r) => `${formatEUR(r.current_rent)} → ${formatEUR(r.agreed_rent || r.new_rent)}`;

// Sendungsverfolgung: Nummer, Status, Abruf
export function TrackingInfo({ r, onChanged }) {
  const sh = r.shipment;
  const [busy, setBusy] = useState(false);
  const isSent = ["sent", "consent_received", "agreed", "partially_agreed", "rejected", "applied"].includes(normStatus(r.status));
  if (!isSent || (!sh && !isRegistered(r.dispatch?.method))) return null;
  const edit = async () => {
    const n = window.prompt("Sendungsnummer des Einschreibens:", sh?.tracking_number || "");
    if (!n) return;
    try { await api.put(`/rent-increase/requests/${r.id}/tracking`, { tracking_number: n }); onChanged?.(); } catch (e) { err(e); }
  };
  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rent-increase/requests/${r.id}/tracking/refresh`);
      toast.success(data.shipment.delivered_at ? `Zugestellt am ${formatDate(data.shipment.delivered_at)}` : (data.shipment.status_text || "Status aktualisiert"));
      onChanged?.();
    } catch (e) { err(e); }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px]" data-testid={`ri-tracking-${r.id}`}>
      <span className="text-slate-500">Sendung:</span>
      {sh?.tracking_number
        ? <a href={sh.url} target="_blank" rel="noreferrer" className="font-mono text-teal-700 hover:underline" data-testid={`ri-tracking-number-${r.id}`}>{sh.tracking_number}</a>
        : <span className="text-amber-700">keine Nummer erfasst</span>}
      {sh?.delivered_at && <span className="rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-1.5" data-testid={`ri-tracking-delivered-${r.id}`}>zugestellt {formatDate(sh.delivered_at)}</span>}
      {sh && !sh.delivered_at && (sh.status_text || sh.not_found) && <span className="text-slate-600" data-testid={`ri-tracking-status-${r.id}`}>{sh.status_text || "noch keine Daten"}</span>}
      {sh?.last_checked_at && <span className="text-slate-400">geprüft {new Date(sh.last_checked_at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}</span>}
      {sh?.tracking_number && !sh.delivered_at && <button type="button" className="text-teal-700 hover:underline" onClick={refresh} disabled={busy} data-testid={`ri-tracking-refresh-${r.id}`}>{busy ? "Prüfe…" : "Jetzt prüfen"}</button>}
      <button type="button" className="text-slate-500 hover:underline" onClick={edit} data-testid={`ri-tracking-edit-${r.id}`}>{sh?.tracking_number ? "ändern" : "erfassen"}</button>
    </div>
  );
}

const num = (v) => { const t = String(v).trim(); return Number(t.includes(",") ? t.replace(/\./g, "").replace(",", ".") : t); };

export function ChangeDialog({ req, open, onClose, onDone }) {
  const [info, setInfo] = useState(null);
  const [f, setF] = useState({ new_rent: "", effective_date: "", extra_text: "", force_replace: false });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    let alive = true;
    api.get(`/rent-increase/requests/${req.id}/change-info`).then(({ data }) => {
      if (!alive) return;
      setInfo(data);
      setF({ new_rent: String(data.new_rent ?? "").replace(".", ","), effective_date: (data.effective_date || "").slice(0, 10), extra_text: data.extra_text || "", force_replace: false });
    }).catch(err);
    return () => { alive = false; };
  }, [open, req.id]);
  const nr = num(f.new_rent);
  const amend = !!info?.is_amendment && !info?.sent;
  const textField = info?.sent || amend;
  const ref = amend ? { rent: info.parent_new_rent, eff: info.parent_effective_date } : { rent: info?.new_rent, eff: info?.effective_date };
  const effChanged = info && f.effective_date && f.effective_date !== (ref.eff || "").slice(0, 10);
  const canReduce = nr < Number(ref.rent) && !effChanged;
  const mode = !textField ? "draft" : (!f.force_replace && canReduce ? "reduce" : "replace");
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rent-increase/requests/${req.id}/change`, { new_rent: nr, effective_date: f.effective_date || null, force_replace: f.force_replace, ...(textField ? { extra_text: f.extra_text } : {}) });
      toast.success(data.mode === "draft_updated" ? (amend ? "Änderungsschreiben aktualisiert – bisheriger Druck intern storniert, erneute Freigabe nötig" : "Geändert – bisheriges Schreiben intern storniert. Bitte neu erstellen und zur Freigabe vorlegen.") : `Änderungsschreiben Az. ${data.case_number} angelegt – nach Freigabe drucken und versenden`);
      onDone(); onClose();
    } catch (e) { err(e); }
    setBusy(false);
  };
  const MODE = {
    draft: ["Noch nicht versendet", "Das bisherige Schreiben wird intern storniert. Die Mieterhöhung geht mit den neuen Werten zurück in den Entwurf und muss erneut freigegeben werden. Den Brieftext passen Sie danach über „Entwurf öffnen“ an."],
    reduce: ["Reduzierung – Fristen bleiben", `Änderungsschreiben mit Passus: Das Verlangen wird zu Gunsten des Mieters reduziert. Zustimmungsfrist (${formatDate(info?.consent_deadline)}) und Wirksamkeit bleiben unverändert. Neue Rückantwort liegt bei.`],
    replace: ["Ersetzung – neue Zustimmungsfrist", "Änderungsschreiben mit Passus: Das bisherige Verlangen wird zurückgenommen und durch das geänderte ersetzt (vollständige Begründung). Zustimmungsfrist und Wirksamkeit werden ab Zugang neu berechnet."],
  }[mode];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="ri-change-dialog">
        <DialogHeader>
          <DialogTitle>Mieterhöhung ändern</DialogTitle>
          <DialogDescription>Bisher: {formatEUR(info?.current_rent)} → {formatEUR(info?.new_rent)} ab {formatDate(info?.effective_date)}</DialogDescription>
        </DialogHeader>
        {!info ? <p className="text-sm text-slate-500">Lade…</p> : <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Neue Nettokaltmiete (€)</Label><Input value={f.new_rent} onChange={(e) => setF({ ...f, new_rent: e.target.value })} data-testid="ri-change-rent" />
              {info.max_rent && <div className="text-[11px] text-slate-500 mt-0.5">höchstens {formatEUR(info.max_rent)} ({info.limited_by})</div>}</div>
            <div><Label className="text-xs">Wirksam ab (Monatserster)</Label><Input type="date" value={f.effective_date} onChange={(e) => setF({ ...f, effective_date: e.target.value })} data-testid="ri-change-effective" />
              {info.earliest_effective_date && <div className="text-[11px] text-slate-500 mt-0.5">frühestens {formatDate(info.earliest_effective_date)}</div>}</div>
          </div>
          {amend && <div className="text-[11px] text-slate-600">Änderungsschreiben noch nicht versendet – ein bereits gedrucktes Exemplar wird intern storniert. Bezug bleibt das Verlangen Az. {req.previous?.case_number} ({formatEUR(info.parent_new_rent)} ab {formatDate(info.parent_effective_date)}).</div>}
          {textField && <div><Label className="text-xs">Ergänzender Text / Begründung im Änderungsschreiben (optional)</Label>
            <Textarea rows={3} value={f.extra_text} onChange={(e) => setF({ ...f, extra_text: e.target.value })} data-testid="ri-change-text" /></div>}
          {textField && canReduce && <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={f.force_replace} onChange={(e) => setF({ ...f, force_replace: e.target.checked })} data-testid="ri-change-force-replace" /> Trotzdem ersetzen (neue Zustimmungsfrist ab Zugang)</label>}
          <div className={`rounded border px-2 py-1.5 text-xs ${mode === "replace" ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-teal-50 border-teal-100 text-teal-900"}`} data-testid="ri-change-mode"><b>{MODE[0]}:</b> {MODE[1]}</div>
          {textField && <div className="text-[11px] text-slate-500">Das Änderungsschreiben braucht erneut eine Freigabe und kann per Post, Einschreiben, Bote oder E-Mail versendet werden.</div>}
        </div>}
        <DialogFooter><Button variant="outline" onClick={onClose}>Abbrechen</Button><Button onClick={save} disabled={busy || !info || !(nr > 0)} data-testid="ri-change-save">{info?.sent ? "Änderungsschreiben anlegen" : "Änderung speichern"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
